import { useEffect, useMemo, useState } from "react";
import { useWorkflowTheme } from "./hooks/useWorkflowTheme";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import "./workflows.css";
import WorkflowListRail from "./WorkflowListRail";
import WorkflowPackageControls from "./WorkflowPackageControls";
import WorkflowToolbar from "./WorkflowToolbar";
import WorkflowCanvas from "./canvas/WorkflowCanvas";
import { useCanvasGraph } from "./canvas/useCanvasGraph";
import { useResizableWidth } from "./hooks/useResizableWidth";
import ResizeHandle from "./ResizeHandle";
import NodePalette from "./NodePalette";
import NodePropertiesPanel from "./NodePropertiesPanel";
import RunSummaryPanel from "./RunSummaryPanel";
import RunHistoryPanel from "./RunHistoryPanel";
import MeetingTimeInputModal from "./MeetingTimeInputModal";
import ActivationConfirmDialog from "./ActivationConfirmDialog";
import { useWorkflows } from "./hooks/useWorkflows";
import { useWorkflowRun } from "./hooks/useWorkflowRun";
import { useAlert } from "../functions/useAlert";
import { fetchWorkflowTools } from "../services/workflow.service";
import { describeTriggerClause } from "./triggerDescriptions";
import type { NodeDef, WorkflowToolCatalog } from "./workflow.types";

export default function WorkflowsTab() {
  const [setAlert] = useAlert();
  const {
    workflows,
    refresh,
    loading,
    selectedId,
    setSelectedId,
    selectedWorkflow,
    applyWorkflowUpdate,
    createNewWorkflow,
    generateNewWorkflow,
    renameWorkflow,
    updateDescription,
    updateTriggerConfig,
    duplicateWorkflowEntry,
    deleteWorkflowEntry,
    activateWorkflowEntry,
    deactivateWorkflowEntry,
    loadExampleWorkflows,
    isSeeding,
  } = useWorkflows();

  const [catalog, setCatalog] = useState<WorkflowToolCatalog | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(true);

  useEffect(() => {
    fetchWorkflowTools()
      .then((res) => setCatalog(res?.data || { triggers: [], actions: [], logic: [], conditionFields: [] }))
      .catch(() => setAlert("Failed to load the workflow step catalog", "error"))
      .finally(() => setCatalogLoading(false));
  }, [setAlert]);

  const nodeDefsById = useMemo(() => {
    const map: Record<string, NodeDef> = {};
    if (catalog) {
      [...catalog.triggers, ...catalog.actions, ...catalog.logic].forEach((def) => {
        map[def.subtype] = def;
      });
    }
    return map;
  }, [catalog]);

  const graph = useCanvasGraph({ workflow: selectedWorkflow, onPersisted: applyWorkflowUpdate });
  const {
    run,
    isRunning,
    needsInput,
    isStarting,
    isResuming,
    startRun,
    stopRun,
    resumeRun,
    showSummary,
    closeSummary,
  } = useWorkflowRun(selectedWorkflow?._id ?? null);

  const [pendingActivation, setPendingActivation] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [inputModalDismissed, setInputModalDismissed] = useState(false);

  useEffect(() => {
    setInputModalDismissed(false);
  }, [run?._id]);

  const pendingInputStepLabel = run?.pendingInput?.label || "this step";

  const handleRequestToggleActive = (nextEnabled: boolean) => {
    if (!selectedWorkflow) return;
    if (!nextEnabled) {
      deactivateWorkflowEntry(selectedWorkflow._id);
      return;
    }
    if (selectedWorkflow.hasConfirmedActivation) {
      activateWorkflowEntry(selectedWorkflow._id);
      return;
    }
    setPendingActivation(true);
  };

  const handleConfirmActivation = async () => {
    if (!selectedWorkflow) return;
    setIsActivating(true);
    const updated = await activateWorkflowEntry(selectedWorkflow._id);
    setIsActivating(false);
    if (updated) {
      setPendingActivation(false);
      setAlert("Workflow activated — it will now run automatically.", "success");
    }
  };

  const panelNode = graph.nodes.find((n) => n.id === graph.panelNodeId) || null;
  const { theme, toggleTheme } = useWorkflowTheme();

  // Both side panels are drag-resizable — shrink either to give the canvas
  // more room when working with a bigger workflow, or the directory more
  // room when managing a long list of them. Widths persist across visits.
  const rail = useResizableWidth({ storageKey: "wf-rail-width", defaultWidth: 260, min: 180, max: 480 });
  const propertiesPanel = useResizableWidth({
    storageKey: "wf-panel-width",
    defaultWidth: 300,
    min: 240,
    max: 560,
    invert: true,
  });

  if (loading) {
    return (
      <Box className="wf-root" data-wf-theme={theme} sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (workflows.length === 0) {
    return (
      <Box
        className="wf-root"
        data-wf-theme={theme}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 10,
          gap: 2,
          textAlign: "center",
        }}
      >
        <PlaylistAddCheckIcon sx={{ fontSize: 64, color: "primary.main", opacity: 0.4 }} />
        <Typography variant="h6">No workflows yet</Typography>
        <WorkflowPackageControls catalog={catalog} onImported={refresh} />
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
          Workflows chain the app&rsquo;s AI actions into an automation that runs on a trigger, with no
          per-step approval once activated.
        </Typography>
        <Button variant="contained" disabled={isSeeding} onClick={loadExampleWorkflows}>
          {isSeeding ? "Loading…" : "Load an example workflow"}
        </Button>
      </Box>
    );
  }

  return (
    <Box
      className="wf-root"
      data-wf-theme={theme}
      sx={{
        display: "flex",
        height: "calc(100vh - 260px)",
        minHeight: 480,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <WorkflowListRail
        workflows={workflows}
        loading={false}
        selectedId={selectedId}
        triggerOptions={catalog?.triggers || []}
        width={rail.width}
        onSelect={setSelectedId}
        onRename={renameWorkflow}
        onUpdateDescription={updateDescription}
        onDuplicate={duplicateWorkflowEntry}
        onDelete={deleteWorkflowEntry}
        onCreate={createNewWorkflow}
        onGenerate={generateNewWorkflow}
      />
      <ResizeHandle onPointerDown={rail.startDragging} />

      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <WorkflowPackageControls catalog={catalog} workflow={selectedWorkflow ? { ...selectedWorkflow, nodes: graph.nodes, edges: graph.edges } : null} onImported={refresh} />
        {selectedWorkflow ? (
          <>
            <WorkflowToolbar
              workflow={selectedWorkflow}
              isRunning={isRunning}
              isStarting={isStarting}
              onRename={(name) => renameWorkflow(selectedWorkflow._id, name)}
              onUpdateDescription={(description) => updateDescription(selectedWorkflow._id, description)}
              onRequestToggleActive={handleRequestToggleActive}
              onRun={(context) => startRun(context)}
              onStop={stopRun}
              onDuplicate={() => duplicateWorkflowEntry(selectedWorkflow._id)}
              onSimulateTrigger={() => startRun({ meetingId: "demo", clientId: "demo" })}
              onOpenHistory={() => setHistoryOpen(true)}
              theme={theme}
              onToggleTheme={toggleTheme}
            />
            <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
              <WorkflowCanvas
                graph={graph}
                nodeDefsById={nodeDefsById}
                conditionFields={catalog?.conditionFields || []}
                run={run}
              />
              <ResizeHandle onPointerDown={propertiesPanel.startDragging} />
              <Box
                sx={{
                  width: propertiesPanel.width,
                  flexShrink: 0,
                  borderLeft: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                  overflowY: "auto",
                }}
              >
                {panelNode ? (
                  <NodePropertiesPanel
                    node={panelNode}
                    def={nodeDefsById[panelNode.subtype]}
                    nodes={graph.nodes}
                    edges={graph.edges}
                    nodeDefsById={nodeDefsById}
                    conditionFields={catalog?.conditionFields || []}
                    onChange={(patch) => graph.updateNodeConfig(panelNode.id, patch)}
                    triggerConfig={selectedWorkflow?.trigger?.config}
                    onChangeTriggerConfig={(config) => selectedWorkflow && updateTriggerConfig(selectedWorkflow._id, config)}
                    onClose={() => graph.openPanel(null)}
                  />
                ) : (
                  <NodePalette
                    catalog={catalog}
                    loading={catalogLoading}
                    hint={graph.paletteHint}
                    onAdd={(def) => graph.addNode(def)}
                  />
                )}
              </Box>
            </Box>
          </>
        ) : (
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Select a workflow to edit it.
            </Typography>
          </Box>
        )}
      </Box>

      {selectedWorkflow && (
        <>
          <RunSummaryPanel open={showSummary} run={run} onClose={closeSummary} />
          <RunHistoryPanel
            open={historyOpen}
            workflowId={selectedWorkflow._id}
            workflowName={selectedWorkflow.name}
            onClose={() => setHistoryOpen(false)}
          />
          {selectedWorkflow && run?.pendingInput && (
            <MeetingTimeInputModal
              open={needsInput && !inputModalDismissed}
              workflowName={selectedWorkflow.name}
              stepLabel={pendingInputStepLabel}
              suggested={run.pendingInput.suggested}
              isSubmitting={isResuming}
              onCancel={() => setInputModalDismissed(true)}
              onConfirm={(fields) => resumeRun(fields)}
            />
          )}
          <ActivationConfirmDialog
            open={pendingActivation}
            workflowName={selectedWorkflow.name}
            triggerDescription={describeTriggerClause(selectedWorkflow.trigger?.type)}
            actionLabels={graph.nodes
              .filter((n) => n.type === "action")
              .map((n) => n.config.label || nodeDefsById[n.subtype]?.label || n.subtype)}
            isSubmitting={isActivating}
            onCancel={() => setPendingActivation(false)}
            onConfirm={handleConfirmActivation}
          />
        </>
      )}
    </Box>
  );
}
